import {isNil, join, pipe, reject} from 'ramda';
import {isNilOrEmpty, isNonEmptyString} from 'ramda-adjunct';

import {GetApiMakeModelWithMakeApiResponse} from '@dms/api/shared';
import {i18n} from '@dms/i18n';

import {Nullish} from 'shared/web';

type Vehicle = {
  make?: string | null;
  modelFamily?: string | null;
  trim?: string | null;
  variant?: string | null;
  title?: string | null;
};

export interface VehicleLabels {
  make: string | undefined;
  modelFamily: string | undefined;
  trim: string | undefined;
  variant: string | undefined;
}

/**
 * Resolves the display labels for a vehicle from the make-model catalogue.
 *
 * Returns the localized (or default_label fallback) label for make and modelFamily.
 * `trim` and `variant` are passed through from the vehicle as-is since they are
 * not part of the make-model catalogue.
 *
 * Returns `undefined` for a given field if it cannot be resolved.
 */
export function getVehicleLabels(
  vehicle: Vehicle,
  makeModels?: GetApiMakeModelWithMakeApiResponse | Nullish
): VehicleLabels {
  const language = i18n?.resolvedLanguage ?? 'en';
  const makeModel = makeModels?.find((entry) => entry.make === vehicle.make);

  return {
    make: isNil(makeModel) ? undefined : getLabel(makeModel, language),
    modelFamily: isNil(makeModel)
      ? undefined
      : getLabel(
          makeModel.models?.find((model) => model.model === vehicle.modelFamily),
          language
        ),
    trim: vehicle.trim ?? undefined,
    variant: vehicle.variant ?? undefined,
  };
}

export function getVehicleTitle(
  vehicle: Vehicle,
  makeModels?: GetApiMakeModelWithMakeApiResponse | Nullish
) {
  if (isNonEmptyString(vehicle.title)) {
    return vehicle.title;
  }

  const makeModel = makeModels?.find((entry) => entry.make === vehicle.make);

  if (isNil(makeModel)) {
    return null;
  }

  const labels = getVehicleLabels(vehicle, makeModels);

  return pipe(
    reject(isNilOrEmpty),
    join(' ')
  )([labels.make, labels.modelFamily, labels.trim, labels.variant]);
}

type LabelType = {
  default_label: string;
  labels?: {
    label: string;
    label_short?: string | null;
    language: string;
  }[];
};

const getLabel = (vehicleModel: LabelType | Nullish, language: string) =>
  vehicleModel?.labels?.find((label) => label.language.toUpperCase() === language.toUpperCase())
    ?.label ?? vehicleModel?.default_label;
